import { restaurerSansReseau } from '../horsLigne/index.js';
import { queryClient } from '../auth/queryClient.js';
import { data, redirect } from 'react-router';
import { estSessionIndisponible } from '../api/errors.js';
import { chargerSession, trouverGalerie } from '../auth/session.js';
import type { SessionUtilisateur } from '../api/types.js';
import { adresseDeRetour } from '../auth/retour.js';
import { cheminDeLiberation } from '../auth/confinement.js';

/**
 * Exige une session ouverte (mode data de react-router 7, `docs/dev/api.md`
 * § 3) : un 401 de `GET /v1/auth/me` redirige vers `/connexion` en gardant
 * l'adresse visée, pour y revenir après connexion. Une session confinée
 * mène, avec le même retour, à la page qui la libère : `/app/conditions` pour
 * les CGU (ARTO-59), d'abord ; puis `/app/double-authentification` quand la
 * grâce pour l'activer est échue.
 */
export async function exigerSession(
  chemin: string,
  galerieSlug: string | null = null,
): Promise<SessionUtilisateur> {
  const session = await sessionOuSecours(chemin, galerieSlug);
  // Tous les parcours y aboutissent — mot de passe, second facteur, SSO,
  // invitation : chacun finit par un chargeur qui relit la session.
  const liberation = cheminDeLiberation(session, chemin);
  if (liberation) throw redirect(liberation);
  return session;
}

async function sessionOuSecours(
  chemin: string,
  galerieSlug: string | null,
): Promise<SessionUtilisateur> {
  try {
    return await chargerSession(galerieSlug);
  } catch (erreur) {
    // M11 : seul un instantané chiffré valide permet une consultation
    // sans réseau ou serveur indisponible ; un refus 401/403 reste définitif.
    if (estSessionIndisponible(erreur)) {
      const secours = await restaurerSansReseau(queryClient, galerieSlug);
      if (secours) return secours;
      throw data({ raison: RAISON_HORS_LIGNE }, { status: 503 });
    }
    throw redirect(`/connexion?retour=${encodeURIComponent(chemin)}`);
  }
}

/** Raison portée par la réponse 503 d'un chargeur sans réseau. */
export const RAISON_HORS_LIGNE = 'hors_ligne';

export interface DonneesCoquille {
  readonly session: SessionUtilisateur;
  readonly galerieSlug: string;
}

/**
 * Charge la session puis vérifie l'appartenance à la galerie demandée. Un
 * slug inconnu ou non membre redirige vers la première galerie du compte : le
 * 404 « galerie inconnue vs interdite » se joue côté API (`docs/dev/api.md`
 * § 2), pas ici.
 */
export async function chargerCoquille(
  url: string,
  slug: string | undefined,
): Promise<DonneesCoquille> {
  // Chemin, paramètres (`?design=`, `?lang=`, `onglet`, `etat`…) et fragment :
  // la page demandée est rendue telle quelle après la connexion.
  const chemin = adresseDeRetour(url);
  // La galerie fait partie de l'identité : en changer purge les caches
  // métier de la précédente avant que la coquille ne se dessine.
  const session = await exigerSession(chemin, slug ?? null);
  const galerie = trouverGalerie(session, slug);

  if (!galerie) {
    const premiere = session.galeries[0];
    if (premiere?.slug && premiere.slug !== slug) {
      throw redirect(`/${premiere.slug}`);
    }
  }

  return { session, galerieSlug: slug ?? '' };
}
